import { attachSignatureCanvas, typedSignatureDataUrl } from "../signature_canvas"

/**
 * Shared draw/type signature modal.
 *
 * Both PDF signing hooks (the overlay filler and the native-form signature
 * pad) open the same modal; only what happens on Accept differs, so that is
 * the one callback.
 *
 * Options:
 *   isInitials     - initials variant: smaller PNG, no name prefill
 *   initialDataUrl - existing signature to restore on the draw canvas
 *   signerName     - prefills the Type tab (signature only)
 *   consentText    - e-sign consent line rendered above the buttons
 *   onAccept(dataUrl) - called with the PNG data URL ("" when cleared)
 */
const BACKDROP_ID = "sig-modal-backdrop"

// Fixed colours: the modal floats over a light PDF and must stay readable
// whatever theme the surrounding page uses.
const INK = "#111827"
const MUTED = "oklch(0.5 0 0)"
const LINE = "oklch(0.85 0 0)"

export function openSignatureModal({
  isInitials = false,
  initialDataUrl = "",
  signerName = "",
  consentText = "",
  onAccept,
} = {}) {
  removeSignatureModal()

  const title = isInitials ? "Enter Initials" : "Sign Here"
  const typePlaceholder = isInitials ? "Type your initials" : "Type your full name"
  const prefill = isInitials ? "" : (signerName || "").trim()

  const backdrop = document.createElement("div")
  backdrop.id = BACKDROP_ID
  backdrop.style.cssText = `
    position:fixed;top:0;left:0;width:100%;height:100%;
    background:rgba(0,0,0,0.5);z-index:9999;
    display:flex;align-items:center;justify-content:center;
  `

  const modal = document.createElement("div")
  modal.style.cssText = `
    background:oklch(0.98 0 0);border-radius:12px;padding:24px;
    width:min(480px,90vw);box-shadow:0 20px 60px rgba(0,0,0,0.3);
    color:${INK};
  `

  modal.innerHTML = `
    <h3 style="font-size:16px;font-weight:600;margin:0 0 16px;color:${INK};">${title}</h3>
    <div style="display:flex;gap:0;border:1px solid ${LINE};border-radius:8px;overflow:hidden;margin-bottom:12px;">
      <button type="button" data-sig-tab="draw" style="flex:1;padding:8px;font-size:13px;background:white;border:none;cursor:pointer;border-right:1px solid ${LINE};color:${INK};">Draw</button>
      <button type="button" data-sig-tab="type" style="flex:1;padding:8px;font-size:13px;background:white;border:none;cursor:pointer;color:${INK};">Type</button>
    </div>
    <div data-sig-panel="draw">
      <canvas data-sig-canvas style="display:block;width:100%;height:120px;border:1px solid ${LINE};border-radius:6px;background:white;touch-action:none;cursor:crosshair;"></canvas>
    </div>
    <div data-sig-panel="type" style="display:none;">
      <input type="text" data-sig-type-input placeholder="${typePlaceholder}" style="width:100%;padding:8px 12px;border:1px solid ${LINE};border-radius:6px;font-size:14px;margin-bottom:8px;box-sizing:border-box;color:${INK};background:white;" />
      <div data-sig-type-preview style="height:60px;border:1px solid ${LINE};border-radius:6px;background:white;display:flex;align-items:center;padding:0 16px;font-family:cursive;font-size:32px;color:${INK};"></div>
    </div>
    <p data-sig-consent style="display:none;font-size:12px;line-height:1.4;color:${MUTED};margin:16px 0 0;"></p>
    <div style="display:flex;justify-content:space-between;align-items:center;margin-top:16px;">
      <button type="button" data-sig-clear style="padding:6px 12px;font-size:13px;background:none;border:1px solid ${LINE};border-radius:6px;cursor:pointer;color:${INK};">Clear</button>
      <div style="display:flex;gap:8px;">
        <button type="button" data-sig-cancel style="padding:8px 16px;font-size:13px;background:none;border:1px solid ${LINE};border-radius:6px;cursor:pointer;color:${INK};">Cancel</button>
        <button type="button" data-sig-accept style="padding:8px 16px;font-size:13px;background:oklch(0.55 0.2 265);color:white;border:none;border-radius:6px;cursor:pointer;font-weight:500;">Accept</button>
      </div>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const canvas = modal.querySelector("[data-sig-canvas]")
  const typeInput = modal.querySelector("[data-sig-type-input]")
  const typePreview = modal.querySelector("[data-sig-type-preview]")
  const consent = modal.querySelector("[data-sig-consent]")

  if (consentText) {
    consent.textContent = consentText
    consent.style.display = "block"
  }

  const toDataUrl = (name) =>
    typedSignatureDataUrl(
      name,
      isInitials
        ? { width: 200, height: 60, fontSize: 28, padding: 12 }
        : { width: 400, height: 100, fontSize: 44, padding: 12 },
    )

  let mode = "draw"
  let currentDataUrl = initialDataUrl || ""
  let pad = null

  const setMode = (next) => {
    mode = next
    modal.querySelectorAll("[data-sig-tab]").forEach(b => {
      const active = b.dataset.sigTab === mode
      b.style.fontWeight = active ? "600" : "normal"
      b.style.background = active ? "white" : "oklch(0.96 0 0)"
      b.style.color = active ? INK : MUTED
    })
    modal.querySelector('[data-sig-panel="draw"]').style.display = mode === "draw" ? "block" : "none"
    modal.querySelector('[data-sig-panel="type"]').style.display = mode === "type" ? "block" : "none"
  }

  const setTyped = (name) => {
    typePreview.textContent = name
    currentDataUrl = name ? toDataUrl(name) : ""
  }

  modal.querySelectorAll("[data-sig-tab]").forEach(btn => {
    btn.addEventListener("click", () => setMode(btn.dataset.sigTab))
  })

  // A known signer name is the fast path: land on Type with the preview
  // already rendered so Accept is one click away. Drawing stays a tab over.
  const hasExisting = currentDataUrl.startsWith("data:")
  if (prefill && !hasExisting) {
    typeInput.value = prefill
    setTyped(prefill)
    setMode("type")
  } else {
    setMode("draw")
  }

  requestAnimationFrame(() => {
    pad = attachSignatureCanvas(canvas, {
      onStrokeEnd: (dataUrl) => {
        currentDataUrl = dataUrl
      },
    })
    if (hasExisting) pad.restore(currentDataUrl)
    if (mode === "type") typeInput.focus()
  })

  typeInput.addEventListener("input", () => setTyped(typeInput.value.trim()))

  modal.querySelector("[data-sig-clear]").addEventListener("click", () => {
    if (pad) pad.clear()
    typeInput.value = ""
    typePreview.textContent = ""
    currentDataUrl = ""
  })

  modal.querySelector("[data-sig-cancel]").addEventListener("click", removeSignatureModal)
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) removeSignatureModal()
  })

  modal.querySelector("[data-sig-accept]").addEventListener("click", () => {
    if (mode === "draw" && pad) currentDataUrl = pad.toDataURL()
    removeSignatureModal()
    if (onAccept) onAccept(currentDataUrl)
  })
}

export function removeSignatureModal() {
  const el = document.getElementById(BACKDROP_ID)
  if (el) el.remove()
}
